import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

interface LayoutOption<T extends string> {
  template: T;
  name: string;
  description: string;
  preview: React.ReactNode;
}

interface LayoutSelectorModalProps<T extends string> {
  open: boolean;
  onClose: () => void;
  currentLayout?: T;
  layoutOptions: LayoutOption<T>[];
  onSelectLayout: (layoutTemplate: T) => void;
}

const LayoutSelectorModal = <T extends string>({
  open,
  onClose,
  currentLayout,
  layoutOptions,
  onSelectLayout,
}: LayoutSelectorModalProps<T>) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Box>
            <Typography variant='h4' fontWeight={600}>
              Choose Dashboard Layout
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Select a layout that best fits your workflow.
            </Typography>
          </Box>
          <IconButton onClick={onClose} edge='end'>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 5 }}>
        <Grid
          container
          spacing={3}
          columns={layoutOptions.length}
          sx={{ mt: 1 }}
        >
          {layoutOptions.map((layout, i) => {
            const isSelected = currentLayout
              ? layout.template === currentLayout
              : i === 0;

            return (
              <Grid
                item
                key={layout.template}
                md={1}
                sx={{ position: 'relative' }}
              >
                <Paper
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : '2px solid transparent',
                    '&:hover': {
                      elevation: 4,
                      transform: 'translateY(-2px)',
                      borderColor: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.action.hover,
                    },
                  }}
                  onClick={() => onSelectLayout(layout.template)}
                >
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {layout.preview}
                    </Box>

                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Box>
                        <Typography variant='h5' fontWeight={600}>
                          {layout.name}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mt: 0.5 }}
                        >
                          {layout.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </Paper>
                {isSelected && (
                  <Button
                    variant='contained'
                    size='small'
                    disabled
                    sx={{
                      minWidth: '100px',
                      position: 'absolute',
                      background: `${theme.tokens.colors.primary} !important`,
                      right: 8,
                      top: 5,
                    }}
                  >
                    Current
                  </Button>
                )}
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default LayoutSelectorModal;
